'use client';

import CharacterCount from '@tiptap/extension-character-count';
import Document from '@tiptap/extension-document';
import GapCursor from '@tiptap/extension-gapcursor';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import type { EditorView } from '@tiptap/pm/view';
import type { JSONContent} from '@tiptap/react';
import { ReactNodeViewRenderer, useEditor } from '@tiptap/react';
import { useEffect } from 'react';

import {
  MentionExtension,
  mentionPluginKey,
  ParagraphExtension,
  SpoilerExtension,
  StickerExtension,
  StickersExtension,
} from '@zoomit/editor';
import { getCookie, setCookie } from '@zoomit/primitives';

import type { TopicInfoType } from '../../../../types/feedbacks';
import { StickerNodeView } from '../extensions/Stickers/StickerNodeView';

type Args = {
  placeholder: string;
  limit: number;
  defaultValue?: JSONContent[];
  topicInfo?: TopicInfoType;
  enableDraft?: boolean;
};

export const useInitializeFeedbackEditor = (args: Args) => {
  const { placeholder, limit, defaultValue, topicInfo, enableDraft = false } = args;
  const draftCookieKey = `${process.env.NEXT_PUBLIC_TENANT}-${topicInfo?.topicId}-feedback-draft`;

  const handleMentionFocusIndex = (editor: EditorView, e: KeyboardEvent) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'Enter') return;

    const mentionState = mentionPluginKey.getState(editor.state);
    if (!mentionState || !mentionState.active) return;

    // Preventing conflict in mention list
    switch (e.key) {
      case 'ArrowUp':
        return true;
      case 'ArrowDown':
        return true;
      case 'Enter':
        return true;
    }
  };

  const handleSaveOnStorage = (value: JSONContent) => {
    if (!enableDraft) return;
    setCookie(draftCookieKey, JSON.stringify(value), 1 * 60 * 60);
  };

  const handleRemoveDraft = () => {
    setCookie(draftCookieKey, '', -1);
  };

  const editor = useEditor({
    extensions: [
      Document.extend({
        content: 'block+ stickers',
      }),
      Text,
      Placeholder.configure({
        placeholder: ({ editor }) => {
          return (editor.options.editorProps as any).placeholder || placeholder;
        },
      }),
      CharacterCount.configure({
        limit,
      }),
      GapCursor,
      SpoilerExtension,
      History,
      MentionExtension,
      ParagraphExtension,
      StickersExtension,
      StickerExtension.extend({
        addNodeView() {
          return ReactNodeViewRenderer(StickerNodeView, {
            className: 'sticker-node-view',
          });
        },
      }),
    ],
    ...(defaultValue && {
      content: {
        type: 'doc',
        content: defaultValue,
      },
    }),
    editorProps: {
      handleKeyDown: (editor, event) => {
        return handleMentionFocusIndex(editor, event);
      },
    },
    onUpdate: ({ editor }) => {
      handleSaveOnStorage(editor.getJSON());
    },
  });

  useEffect(() => {
    // @ts-ignore
    editor?.setOptions({ editorProps: { placeholder } });
  }, [placeholder, editor]);

  useEffect(() => {
    if (!enableDraft) return;
    const draft = getCookie(draftCookieKey);
    if (draft && editor && editor.isEmpty) {
      editor.commands.setContent(JSON.parse(draft));
    }
  }, [editor, draftCookieKey, enableDraft]);

  return { editor, handleRemoveDraft };
};
